import React, {useContext, useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styles from './sidebar.module.css'
import {faBoxesStacked, faBuilding, faChartBar, faChevronDown, faChevronUp, faClockRotateLeft, faDatabase, faEdit, faGears, faLocationDot, faMicrochip, faMoneyBill, faPlug, faPlus, faSliders, faTrash, faX, faFilePdf, faWrench, faVideo, faIndustry} from "@fortawesome/free-solid-svg-icons";
import {Link, useLocation, useNavigate} from "react-router-dom";
import MyContext from "../../Context";
import Modal from "../modal/modal";
import {TextInput} from "../input/inputs";
import axiosInstance from "../../AxiosInstance";
import {useTranslation} from "react-i18next";

function DashboardActions({selectedDashboard, onEdit, onDelete}) {
    const {t} = useTranslation();
    return (
        <div style={{width: '200px', height: 'auto'}}>
            <div className={styles.popupButton} onClick={onEdit}>
                <FontAwesomeIcon icon={faEdit} color={'#64748B'} className={'me-2'}/>
                <div style={{color: '#334155'}}>{t('sidebar.rename')}</div>
            </div>
            <div className={styles.popupButton} onClick={onDelete}>
                <FontAwesomeIcon icon={faTrash} color={'#f64a4a'} className={'me-2'}/>
                <div style={{color: '#f64a4a'}}>{t('sidebar.delete')}</div>
            </div>
        </div>
    )
}

export default function Sidebar({sidebarIndex, setShouldOpen}) {
    const {t} = useTranslation();
    const [showProjects, setShowProjects] = useState(false);
    const offices = JSON.parse(localStorage.getItem('offices'));
    const project = JSON.parse(localStorage.getItem('project'));
    const [showDashboard, setShowDashboards] = useState(true);
    const [dashboard_id, setDashboardID] = useState(parseInt(localStorage.getItem('dashboard_id')));
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [form, setForm] = useState({});
    const [showDashboardActions, setShowDashboardActions] = useState(false);
    const [selectedDashboard, setSelectedDashboard] = useState({});
    const popupRef = useRef();
    const [mousePosition, setMousePosition] = useState({});

    // Rename dashboard modal
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameForm, setRenameForm] = useState({name: ''});

    const {dashboards, corporateGroup, user} = useContext(MyContext);

    const hasPermission = (module) => {
        if (!module) return true;
        if (user?.is_superuser) return true;
        if (!user?.role?.permissions) return true;
        const perm = user.role.permissions.find(p => p.module === module);
        return perm?.can_view ?? false;
    };

    const FEATURE_FLAG_BY_MODULE = {
        gps: 'allows_gps_tracking',
        scada: 'allows_scada',
    };
    const cgLoaded = corporateGroup && Object.keys(corporateGroup).length > 0;
    const isFeatureEnabled = (module) => {
        const flag = FEATURE_FLAG_BY_MODULE[module];
        if (!flag) return true;
        if (!cgLoaded) return false;
        return !!corporateGroup[flag];
    };

    const items = [
        {name: t('nav.resources'), faIcon: faBoxesStacked, link: "/resources", module: "assets"},
        {name: t('nav.gps'), faIcon: faLocationDot, link: "/gps", module: "gps"},
        {name: t('nav.devices'), faIcon: faMicrochip, link: "/device_list", module: "devices"},
        {name: t('nav.offices'), faIcon: faBuilding, link: "/offices", module: "offices"},
        {name: t('nav.operations'), faIcon: faGears, link: "/operations", module: "operations"},
        {name: t('nav.payments'), faIcon: faMoneyBill, link: "/settings/payments", module: "payments"},
        {name: t('nav.historic'), faIcon: faClockRotateLeft, link: "/historic", module: "historical_data"},
        {name: t('nav.historicalData'), faIcon: faDatabase, link: "/historical-data", module: "historical_data"},
        {name: t('nav.connections'), faIcon: faPlug, link: "/connections", module: "connections"},
        {name: t('nav.cameras'), faIcon: faVideo, link: "/cameras", module: "cameras"},
        {name: t('nav.production'), faIcon: faChartBar, link: "/production/comparison", module: "production"},
        {name: t('nav.reports'), faIcon: faFilePdf, link: "/reports", module: "reports"},
        {name: t('nav.maintenance'), faIcon: faWrench, link: "/maintenance", module: "maintenance"},
        {name: t('nav.scada'), faIcon: faIndustry, link: "/scada", module: "scada"},
    ]

    const changeOffice = (office) => {
        localStorage.setItem('project', JSON.stringify(office));
        localStorage.setItem('project_id', office.id);
        setShowProjects(false);
        window.location.reload();
    }

    const navigate = useNavigate();
    const location = useLocation();

    const dashboard = () => {
        if(corporateGroup?.allows_multi_company) {
            navigate('/');
        } else {
            navigate('/resources');
        }
    }

    const changeDashboard = (dashboard) => {
        localStorage.setItem('dashboard_id', dashboard.id);
        localStorage.setItem('dashboard_name', dashboard.name);
        setDashboardID(dashboard.id);
        navigate('/dashboards')
        window.location.reload();
    }

    const createDashboard = () => {
        axiosInstance.post('dashboard/', {
            name: form.name,
            company_id: parseInt(localStorage.getItem('company_id'))
        }).then(() => {
            window.location.reload();
        })
    }

    const renameDashboard = () => {
        axiosInstance.put(`dashboard/${selectedDashboard.id}/`, {
            name: renameForm.name
        }).then(() => {
            window.location.reload();
        })
    }

    const deleteDashboard = () => {
        axiosInstance.delete(`dashboard/${selectedDashboard.id}/`).then(() => {
            window.location.reload();
        })
    }

    const handleEditDashboard = () => {
        setRenameForm({name: selectedDashboard.name});
        setShowDashboardActions(false);
        setShowRenameModal(true);
    }

    const handleDeleteDashboard = () => {
        setShowDashboardActions(false);
        deleteDashboard();
    }

    const handleOutsideClick = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowDashboardActions(false);
        }
    };

    useEffect(() => {
        if (showDashboardActions) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showDashboardActions]);

    return (
        <div className={`${styles.sidebar}`}>
            {showDashboardActions &&
                <div ref={popupRef} className={styles.popUpModule} style={{top: mousePosition.y + 10}}>
                    <DashboardActions
                        selectedDashboard={selectedDashboard}
                        onEdit={handleEditDashboard}
                        onDelete={handleDeleteDashboard}
                    />
                </div>
            }

            <Modal show={showDashboardModal} setter={setShowDashboardModal} name={'Create Dashboard'} onClick={() => {
                createDashboard();
            }
            } buttonName={'Create'}>
                <TextInput name={'name'} form={form} setForm={setForm}/>
            </Modal>

            <Modal show={showRenameModal} setter={setShowRenameModal} name={'Renombrar Dashboard'} onClick={() => {
                renameDashboard();
            }} buttonName={'Guardar'}>
                <TextInput name={'name'} form={renameForm} setForm={setRenameForm}/>
            </Modal>

            <div>
                <div className={`pb-3 ${styles.corp}`}>
                    <div className={`${styles.closeSidebar} pt-4`} role={'button'} onClick={() => {
                        setShouldOpen(false);
                    }}>
                        <FontAwesomeIcon icon={faX} color={"#FFF"}/>
                    </div>
                    <div className={'pt-4 d-flex align-items-center justify-content-center'} onClick={() => dashboard()} role={'button'}>
                        {corporateGroup?.image ? (
                            <img
                                src={corporateGroup.image.image}
                                alt={'corp_logo'}
                                style={{
                                    maxHeight: '40px',
                                    maxWidth: '120px',
                                    objectFit: 'contain',
                                    borderRadius: '6px',
                                    padding: '4px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }}
                            />
                        ) : (
                            <div style={{color: "#FFF", fontSize: '16px', fontWeight: 600}}>{corporateGroup?.name}</div>
                        )}
                    </div>

                    {(hasPermission('offices') || offices?.length > 1) ? (
                        <>
                            <div className={`mt-3 d-flex align-items-center justify-content-between mx-3 ${styles.dropdownButton}`} role={'button'} onClick={() => setShowProjects(!showProjects)}>
                                <div className={'d-flex'}>
                                    <img src={'/sidebar/building.png'} width={'24px'} alt={'corp_logo'} className={'me-2'}/>
                                    <div>{project && project.name}</div>
                                </div>
                                <img src={'/chevron_up_down.png'} width={'8px'} alt={'corp_logo'}/>
                            </div>

                            <div className={`${styles.dropdown} ${!showProjects && styles.hideDropdown}`}>
                                <div className={'pt-2 pb-1'}>Change Office</div>
                                {offices?.map((office, index) => (
                                    <div key={index} className={`d-flex align-items-center py-2 justify-content-between ${project?.id === office.id && styles.dropdownActive}`} role={'button'} onClick={() => changeOffice(office)}>
                                        <div className={'d-flex align-items-center'}>
                                            <img src={'/sidebar/building.png'} width={'24px'} alt={'corp_logo'} className={'me-2'}/>
                                            <div>{office.name}</div>
                                        </div>
                                        <div>
                                            <img src={'/check.png'} width={'16px'} className={`${project?.id !== office.id && styles.hideDropdown}`}/>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={'mt-3 d-flex align-items-center mx-3'} style={{padding: '5px 0'}}>
                            <img src={'/sidebar/building.png'} width={'24px'} alt={'corp_logo'} className={'me-2'}/>
                            <div style={{color: '#FFF'}}>{project && project.name}</div>
                        </div>
                    )}
                </div>

                {hasPermission('dashboards') && <ul className={`${styles.sections} pb-2`} style={{borderBottom: '2px solid #1E293B'}}>

                    <li style={{cursor: "pointer"}}>
                        <div className="rounded-2" style={{padding: '5px 8px 5px 1px', borderRadius: '6px'}} onClick={() => setShowDashboards(!showDashboard)}>
                            <div className="d-flex align-items-center ps-2" style={{padding: '5px 8px 5px 12px', borderRadius: '6px'}}>
                                <img src={'/sidebar/dashboard.png'} alt={'sidebar_icon'} width={'24px'}/>
                                <div style={{color: '#FFF'}} className={'ps-2'}>Dashboards</div>
                                {showDashboard ?
                                    <FontAwesomeIcon icon={faChevronUp} color={'#FFFFFF'} size={'xs'}/>
                                    :
                                    <FontAwesomeIcon icon={faChevronDown} color={'#FFFFFF'} size={'xs'}/>
                                }
                            </div>
                        </div>
                    </li>
                    {showDashboard && dashboards?.map((dashboard, index) => (
                        <li style={{paddingTop: "0", paddingBottom: "0"}} key={index} className={'ms-2'} onClick={() => changeDashboard(dashboard)} onContextMenu={(event) => {
                            event.preventDefault();
                            setShowDashboardActions(true);
                            setSelectedDashboard(dashboard);
                            setMousePosition({ x: event.clientX, y: event.clientY });
                        }}>
                            <div className={`rounded-2 ${styles.dashboardHoverable}`}>
                                <div className="d-flex align-items-center ms-3 py-1 ps-2" style={dashboard_id === dashboard.id ? {borderLeft: '3px solid #4F46E5'} : {borderLeft: '2px solid #334155'}}>
                                    <div><span>{dashboard.name}</span></div>
                                </div>
                            </div>
                        </li>
                    ))}
                    <li className={'ms-2 mt-2'} onClick={() => setShowDashboardModal(true)} >
                        <div className={`rounded-2 ${styles.dashboardHoverable}`}>
                            <div className={'d-flex justify-content-start align-items-center text-center'}>
                                <div className={'d-flex justify-content-center align-items-center'}
                                    style={{
                                        textAlign: 'center',
                                        color: '#CBD5E1',
                                        backgroundColor: '#1E293B',
                                        borderRadius: '6px',
                                        width: '34px',
                                        height: '31px'
                                }}>
                                    <FontAwesomeIcon className={'text-center'} style={{margin: 0}} icon={faPlus} color={'#CBD5E1'}/>
                                </div>
                                <div className={'ps-3'} style={{color: '#CBD5E1'}}>Create Dashboard</div>
                            </div>
                        </div>
                    </li>
                </ul>}

                <ul className={`${styles.sections}`}>
                    {items.filter(item => hasPermission(item.module) && isFeatureEnabled(item.module)).map((item) => (
                        <li key={item.link}>
                            <div className={`rounded-2 ${styles.hoverable} ${(location.pathname === item.link || location.pathname.startsWith(item.link + '/')) && styles.activeSelection}`} style={{padding: '5px 8px 5px 12px'}}>
                                <Link to={item.link} className="d-flex align-items-center" onClick={() => {
                                    localStorage.setItem('dashboard_id', '0');
                                }}>
                                    {item.faIcon
                                        ? <FontAwesomeIcon icon={item.faIcon} style={{width: '24px', height: '24px'}} color={'#94A3B8'}/>
                                        : <img src={item.icon} alt={'sidebar_icon'} width={'24px'}/>
                                    }
                                    <div style={{margin: '5px 8px 5px 12px'}}><span>{item.name}</span></div>
                                </Link>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles.bottomDiv}>
                <Link className={`${styles.configuration}`} to={'/settings/general'} onClick={() => {
                    localStorage.setItem('dashboard_id', '0');
                }}>
                    <FontAwesomeIcon icon={faSliders} color={'#798BA3'}/>
                    <div style={{color: "#798BA3"}}>Configuration</div>
                </Link>
                <div className={styles.borderBottom}/>
                <img src={'/mid.png'} alt={'logo'} className={'my-4'} height={'32px'}/>
            </div>
        </div>
    )
}